import type {
  AgenticLogicActiveRepairRun,
  AgenticLogicRepairRunStatus,
  AgenticLogicRuleDto,
  AgenticLogicRulePublishJobDto,
} from "@intelligems/ig-types";
import type { GateStripOperation } from "@src/features/agenticCodegen/workspace/components/02-GateRail/GateProgressStrip";

/** A completed publish is authoritative while the rule and version reads refresh. */
export function publishedJobCoversCurrentDraft({
  job,
  isDirty,
  draftLastUpdateTs,
  ruleLastUpdateTs,
}: {
  job:
    | Pick<
        AgenticLogicRulePublishJobDto,
        "status" | "mode" | "resultingVersionId" | "finishedAtTs"
      >
    | undefined;
  isDirty: boolean;
  draftLastUpdateTs: number | null | undefined;
  ruleLastUpdateTs: number;
}): boolean {
  return (
    job?.status === "succeeded" &&
    job.mode === "publish" &&
    job.resultingVersionId !== null &&
    job.finishedAtTs !== null &&
    !isDirty &&
    (draftLastUpdateTs ?? 0) <= job.finishedAtTs &&
    ruleLastUpdateTs <= job.finishedAtTs
  );
}

export function shouldShowRulePublish({
  operationBusy,
  publishedCurrentDraft,
  hasUnpublishedChanges,
  ruleState,
  checkPhase,
}: {
  operationBusy: boolean;
  publishedCurrentDraft: boolean;
  hasUnpublishedChanges: boolean;
  ruleState: AgenticLogicRuleDto["state"];
  checkPhase: GateStripOperation["phase"];
}): boolean {
  return (
    !operationBusy &&
    !publishedCurrentDraft &&
    (hasUnpublishedChanges || ruleState === "draft" || checkPhase === "failed")
  );
}

/** The operation owns the verdict; individual jobs can be intermediate attempts. */
export function ruleCheckPhase({
  job,
  repairRun,
  busy,
  held,
  stopped,
  hasDraftChanges,
  describesPublishedVersion,
  checksStale = false,
}: {
  job:
    | Pick<AgenticLogicRulePublishJobDto, "status" | "mode" | "gates">
    | undefined;
  repairRun: AgenticLogicActiveRepairRun | null;
  busy: boolean;
  held: boolean;
  stopped: boolean;
  hasDraftChanges: boolean;
  describesPublishedVersion: boolean;
  checksStale?: boolean;
}): GateStripOperation["phase"] {
  if (busy) {
    const isPublishing =
      job?.mode === "publish" &&
      (job.status === "queued" || job.status === "running") &&
      job.gates.some(
        (gate) =>
          gate.gateKey === "lambda_deploy" &&
          (gate.status === "running" || gate.status === "passed")
      );
    return isPublishing ? "publishing" : "checking";
  }
  if (held) {
    if (repairRun?.giveUp?.cause === "manual_publish") return "checked";
    return "review";
  }
  if (
    stopped &&
    repairRun?.status === "abandoned" &&
    repairRun.giveUp?.cause === "draft_changed"
  )
    return "draft";
  if (stopped) return "failed";
  if (describesPublishedVersion) return "published";
  if (checksStale) return "draft";
  if (job?.status === "cancelled") return "stopped";
  if (job?.status === "failed") return "failed";
  if (job?.status === "succeeded" && job.mode === "dry_run") return "checked";
  if (hasDraftChanges || !job) return "draft";
  if (job.status === "succeeded" && job.mode === "publish") return "published";
  return "draft";
}

/**
 * The repair loop's two abnormal endings, as one idea.
 *
 * `gave_up` is a verdict on the rule — the loop tried and the bounds ran out.
 * `abandoned` is the platform standing down: the merchant edited the draft
 * mid-fix, or the supervisor's own sweep forced a run terminal because its
 * worker died and its deadline passed. Both mean the same thing to the
 * workspace — the rule did not publish and nothing is still working on it —
 * and keying the surfaces on `gave_up` alone left the interrupted run with no
 * error surface at all: the strip stopped saying "Atlas is fixing this" and
 * put nothing in its place.
 */
const STOPPED_STATUSES: ReadonlySet<AgenticLogicRepairRunStatus> =
  new Set<AgenticLogicRepairRunStatus>(["gave_up", "abandoned"]);

/**
 * The third ending, and deliberately NOT a member of STOPPED_STATUSES.
 *
 * `awaiting_approval` is not a verdict on the rule and not the platform
 * standing down: the checks went green. What did not happen is publishing —
 * the only rewrite that passed changed what the rule does, so the loop
 * prepared it instead of shipping it. Calling that "gave up" would blame the
 * rule for a bound the PLATFORM observes, and would hide the one thing the
 * merchant has to do: decide.
 */
export function isRepairAwaitingApproval(
  run: AgenticLogicActiveRepairRun | null
): boolean {
  return run !== null && run.status === "awaiting_approval";
}

/** The run ended without publishing. Nothing is still working on the rule. */
export function didRepairStop(
  run: AgenticLogicActiveRepairRun | null
): boolean {
  return run !== null && STOPPED_STATUSES.has(run.status);
}

/**
 * A stopped repair remains actionable until a successful version is published
 * from that repair or from a later manual attempt.
 *
 * `activeVersionPublishedAtTs` is `undefined` while the version history has
 * not loaded and `null` once it has and there is no live version. The two
 * must not collapse: the rule detail (which carries the stopped run) resolves
 * ahead of the versions query, and treating "not loaded yet" as "never
 * published" flashed the panel on every open of a rule that had long since
 * shipped past its stopped repair.
 */
export function shouldShowRepairGiveUp(
  run: AgenticLogicActiveRepairRun | null,
  activeVersionPublishedAtTs: number | null | undefined
): boolean {
  if (run === null || !didRepairStop(run)) return false;
  if (activeVersionPublishedAtTs === undefined) return false;
  if (activeVersionPublishedAtTs === null) return true;
  if (run.finishedAtTs === null) return true;
  return activeVersionPublishedAtTs < run.finishedAtTs;
}

/** A restore action needs both a saved baseline and an earlier live version. */
export function canOfferRepairBaselineRestore(
  run: AgenticLogicActiveRepairRun | null,
  activeVersionNumber: number | null
): boolean {
  return (
    run !== null &&
    run.baselineDraftS3Key !== null &&
    activeVersionNumber !== null &&
    activeVersionNumber > 1
  );
}

/**
 * The contradiction handed to the gate strip: the strip describes whichever
 * publish job the port tracks, which is not the rule's state — a rule the loop
 * stopped on never shipped, yet the last run beside it can still read "All N
 * checks passed".
 */
export function repairStoppedGateReason(
  run: AgenticLogicActiveRepairRun | null,
  authorName: string
): string | null {
  if (run === null || !didRepairStop(run)) return null;
  if (run.status === "gave_up") {
    return `${authorName} gave up on this rule — it did not publish`;
  }
  return `${authorName} stopped working on this rule before it finished — it did not publish`;
}
